/**
 * postMessage bridge types for PMS <-> OHIF Viewer communication.
 *
 * Scope: events and commands only. Custom OHIF panels fetch PMS data
 * (charting, plans, notes) directly via REST — not through postMessage.
 *
 * Keep this file in sync with:
 *   enamelai-ohif/platform/app/src/utils/imageViewerMessages.ts
 */

// ─── OHIF → PMS (events the viewer sends to the host) ───────────────────────

export interface ViewerClosedEvent {
  type: 'viewer-closed'
}

/**
 * Emitted once the viewer has rendered its first image for the current study.
 * The PMS host uses this to dismiss the "loading" overlay it shows while the
 * iframe boots and fetches/decodes the DICOM, instead of waiting on a timeout.
 */
export interface ViewerReadyEvent {
  type: 'viewer-ready'
}

export interface ReportSubmittedEvent {
  type: 'report-submitted'
  payload: {
    reportId: string
    patientId: string
    studyInstanceUID: string
  }
}

export interface AnnotationSavedEvent {
  type: 'annotation-saved'
  payload: {
    annotationUID: string
    toolName: string
    studyInstanceUID: string
    seriesInstanceUID: string
    sopInstanceUID: string
  }
}

export interface CheckPreviousXraysEvent {
  type: 'check-previous-xrays'
}

export interface ViewTypeChangedEvent {
  type: 'view-type-changed'
  payload: {
    viewType: 'fmx' | 'panoramic'
  }
}

export interface OpenNotesForImageEvent {
  type: 'open-notes-for-image'
  payload: {
    patientId: string
    studyInstanceUID: string
    seriesInstanceUID: string
    sopInstanceUID: string
    imageFilename: string
    studyDate: string
    modality: string
    suggestedTitle: string
  }
}

export interface OpenCloudStorageEvent {
  type: 'open-cloud-storage'
}

export interface PrintReportEvent {
  type: 'print-report'
}

export interface AddFilesEvent {
  type: 'add-files'
}

/**
 * OHIF asks the PMS host to (re)send the current `set-file-metadata`. Used on
 * iframe (re)loads so the viewer doesn't have to rely on the PMS happening to
 * post during the brief window the listener is registered.
 */
export interface RequestFileMetadataEvent {
  type: 'request-file-metadata'
}

export interface DeleteFileRequestEvent {
  type: 'delete-file-request'
  payload: {
    seriesInstanceUID: string
    studyInstanceUID: string
    displaySetInstanceUID?: string
    imageFilename?: string
  }
}

export type OhifToPmsMessage =
  | ViewerClosedEvent
  | ViewerReadyEvent
  | ReportSubmittedEvent
  | AnnotationSavedEvent
  | CheckPreviousXraysEvent
  | ViewTypeChangedEvent
  | OpenNotesForImageEvent
  | OpenCloudStorageEvent
  | PrintReportEvent
  | AddFilesEvent
  | DeleteFileRequestEvent
  | RequestFileMetadataEvent

// ─── PMS → OHIF (commands the host sends into the viewer) ───────────────────

export interface NavigateToStudyCommand {
  type: 'navigate-to-study'
  payload: {
    studyInstanceUID: string
    seriesInstanceUID?: string
  }
}

export interface FileMetadataEntry {
  seriesUID: string
  fileName: string
  originalName: string
  // Optional auxiliary identifiers that allow OHIF to resolve a filename even
  // when the seriesUID it sees has been rewritten server-side (e.g. by the
  // anonymization pipeline). Included when known by the PMS database.
  studyUID?: string
  sopUID?: string
}

export interface SetFileMetadataCommand {
  type: 'set-file-metadata'
  payload: {
    files: FileMetadataEntry[]
  }
}

export type PmsToOhifMessage = NavigateToStudyCommand | SetFileMetadataCommand

// ─── All message types (union) ───────────────────────────────────────────────

export type ImageViewerMessage = OhifToPmsMessage | PmsToOhifMessage

// ─── Protocol constant ──────────────────────────────────────────────────────

export const IMAGE_VIEWER_MESSAGE_SOURCE = 'enamelai-image-viewer' as const

/**
 * Envelope wrapping every message through the bridge.
 * The `source` field prevents collisions with messages from other iframes
 * or third-party scripts.
 */
export interface ImageViewerMessageEnvelope {
  source: typeof IMAGE_VIEWER_MESSAGE_SOURCE
  message: ImageViewerMessage
}

// ─── Guards ──────────────────────────────────────────────────────────────────

export function isImageViewerMessageEnvelope(
  data: unknown,
): data is ImageViewerMessageEnvelope {
  if (typeof data !== 'object' || data === null) return false
  const env = data as ImageViewerMessageEnvelope
  return (
    env.source === IMAGE_VIEWER_MESSAGE_SOURCE &&
    typeof env.message === 'object' &&
    env.message !== null &&
    typeof env.message.type === 'string'
  )
}
